import { useQuery } from "@tanstack/react-query";
import { z } from "zod";

export const CustomerFolderSchema = z.object({
  id: z.string().uuid(),
  name: z.string(),
  assignedAt: z.string(),
  addedByUserId: z.string().uuid().nullable(),
});

export type CustomerFolder = z.infer<typeof CustomerFolderSchema>;

const CustomerFoldersResponseSchema = z.object({
  folders: z.array(CustomerFolderSchema),
});

async function fetchCustomerFolders(customerId: string): Promise<CustomerFolder[]> {
  const response = await fetch(`/api/dashboard/customers/${customerId}/folders`);

  if (!response.ok) {
    throw new Error("Failed to fetch customer folders");
  }

  const data = await response.json();
  const validated = CustomerFoldersResponseSchema.parse(data);
  return validated.folders;
}

export const customerFoldersQueryKey = (customerId: string | null) =>
  ["customers", customerId, "folders"] as const;

export function useCustomerFoldersQuery(customerId: string | null) {
  return useQuery({
    queryKey: customerFoldersQueryKey(customerId),
    queryFn: () => fetchCustomerFolders(customerId!),
    enabled: !!customerId,
    staleTime: 1000 * 60 * 10, // 10 minutes - customer folder assignments are relatively stable
    gcTime: 1000 * 60 * 60, // Keep in cache for 1 hour
    refetchOnWindowFocus: false,
    refetchOnMount: false,
  });
}
